import { formatMiliseconds } from '@playbookux/playbook-web-core';
import _ from 'lodash';
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import Mansory from 'react-masonry-component';

import BarChart from '../../../../../../core/components/charts/bar-chart';
import { getQuotesKeysByProjectId } from './graphql';
import QuotesCard from './quotes-card';

import {
  Card,
  ChartTitle,
  Container,
  Keyword,
  Loader,
  MansoryContainer,
  MansoryGutter,
} from './styles';

const ProjectQuotes = (props) => {
  const {
    project, history, segmentId, isDemoProject,
  } = props;

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const [selectedKeyword, setSelectedKeyword] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [keywords, setKeywords] = useState([]);

  const formatKeyword = (keyword) => {
    if (!keyword) {
      return '';
    }

    if (keyword.length < 3) {
      return _.upperCase(keyword);
    }

    return _.startCase(keyword);
  };

  const keywordsToBarChart = (keywordValue) => {
    const labels = _.map(keywordValue, ({ keyword }) => formatKeyword(keyword));
    const data = _.map(keywordValue, ({ count }) => count);

    return {
      labels,
      datasets: [{
        hidden: false,
        backgroundColor: '#407CD1',
        data,
      }],
    };
  };

  const onQuotesBarClicked = (value) => {
    if (!value) {
      return;
    }

    const { _model: model } = value;
    const { label } = model;

    setSelectedKeyword(_.lowerCase(label));
  };

  useEffect(() => {
    async function getQuotesKeys(demoProject, projectValue) {
      try {
        let quotesKeys = {};

        if (demoProject) {
          const { analyticsQuoteData } = await import('../../../../../demo-project');

          ({ quotesKeys } = analyticsQuoteData);
        } else {
          ({ quotesKeys } = await getQuotesKeysByProjectId(projectValue.id, segmentId));
        }

        const quotesValues = _.groupBy(quotesKeys, 'keyword');

        const keywordsValues = _.chain(quotesValues)
          .reduce((result, value, key) => [
            ...result,
            { keyword: key, count: _.first(value).keywordCount },
          ], [])
          .orderBy(['count'], ['desc'])
          .value();

        setIsLoading(false);
        setQuotes(quotesValues);
        setKeywords(keywordsValues);
        setSelectedKeyword(_.first(keywordsValues) ? _.first(keywordsValues).keyword : null);
      } catch (err) {
        setError(true);
        setIsLoading(false);
      }
    }
    getQuotesKeys(isDemoProject, project, segmentId);
    // do not add project in the dependecy, it will duplicate requests when clicking on charts
  }, [segmentId]);

  const highlight = [selectedKeyword];
  const mentions = selectedKeyword ? _.find(keywords, { keyword: selectedKeyword }).count : 0;

  if (isLoading) {
    return <Loader data-loader="project-quotes-loader" />;
  }

  if (error) {
    return null;
  }

  return (
    <Container>
      <Card>
        <ChartTitle>Click on a keyword or bar below to view quotes</ChartTitle>

        <BarChart
          data={keywordsToBarChart(keywords)}
          height={350}
          onBarClicked={onQuotesBarClicked}
          stepSize={2}
          type="vertical"
          yAxesLabel="# Mentions"
        />

        <Keyword>
          Keyword:
          {formatKeyword(selectedKeyword)}
          {' '}
          (
          {mentions}
          {' '}
          Mentions)
        </Keyword>
      </Card>

      <Mansory
        options={{
          columnWidth: '.mansory-sizer',
          horizontalOrder: true,
          itemSelector: '.mansory-item',
          gutter: '.mansory-gutter-sizer',
          percentPosition: true,
        }}
      >
        <MansoryContainer className="mansory-sizer" />
        <MansoryGutter className="mansory-gutter-sizer" />

        {_.chain(quotes[selectedKeyword])
          .uniqBy('threeSentences')
          .map((quote) => (
            <MansoryContainer
              key={quote.quotesKeyId}
              className="mansory-item"
            >
              <QuotesCard
                taskName={quote.questionGroupName.replace(/task/ig, 'Question')}
                taskDuration={formatMiliseconds(quote.videoTime)}
                highlight={highlight}
                onVideoLinkClicked={() => history.push({
                  pathname: `/projects/${project.id}/videos/${quote.testerId}`,
                  state: { testerId: quote.testerId },
                })}
                firstName={quote.profile?.firstName || ''}
              >
                {quote.threeSentences}
              </QuotesCard>
            </MansoryContainer>
          ))
          .value()}
      </Mansory>
    </Container>
  );
};

ProjectQuotes.propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func,
  }).isRequired,
  project: PropTypes.shape({
    id: PropTypes.string,
  }).isRequired,
  segmentId: PropTypes.string.isRequired,
  isDemoProject: PropTypes.bool.isRequired,
};

export default ProjectQuotes;
